import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../utils/cn';
import type { OrgMembership } from '../../types/auth';

/** The picker shows an organisation's short name, else its name. */
const orgLabel = (org: OrgMembership) => org.short_name?.trim() || org.name;

function OrgSwitcher() {
  const { user, switchOrg, isSwitchingOrg } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dropdownAlign, setDropdownAlign] = useState<'right' | 'left'>('right');
  const ref = useRef<HTMLDivElement>(null);

  const orgs = user?.orgs ?? [];
  const activeOrg = orgs.find(o => o.is_active) ?? orgs[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Flip dropdown to stay on screen
  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownAlign(rect.left >= 220 ? 'right' : 'left');
    }
  }, [open]);

  // Only show when user belongs to more than one org
  if (orgs.length <= 1) return null;

  const handleSwitch = async (orgId: number) => {
    if (orgId === activeOrg?.id || isSwitchingOrg) return;
    setError(null);
    setOpen(false);
    try {
      await switchOrg(orgId);
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Could not switch organisation. Please try again.');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        disabled={isSwitchingOrg}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch organisation"
        className={cn(
          'flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium',
          'bg-white border border-neutral-300 text-neutral-700',
          'hover:bg-neutral-50 hover:border-neutral-400',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          'transition-all duration-150',
          'disabled:opacity-60 disabled:cursor-not-allowed'
        )}
      >
        {isSwitchingOrg ? (
          <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        ) : (
          <Building2 className="h-4 w-4 text-primary-600 flex-shrink-0" />
        )}
        <span className="max-w-[140px] truncate hidden sm:inline">
          {isSwitchingOrg ? 'Switching…' : (activeOrg ? orgLabel(activeOrg) : 'Select org')}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-neutral-400 flex-shrink-0 transition-transform duration-150 hidden sm:block', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className={cn(
            `absolute ${dropdownAlign === 'right' ? 'right-0' : 'left-0'} mt-1 min-w-[220px] rounded-xl bg-white shadow-lg`,
            'border border-neutral-200 py-1 z-50',
            'animate-in fade-in slide-in-from-top-1 duration-100'
          )}
        >
          <p className="px-3 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
            Your organisations
          </p>
          {orgs.map(org => (
            <button
              key={org.id}
              role="option"
              aria-selected={org.is_active}
              onClick={() => handleSwitch(org.id)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left',
                'hover:bg-neutral-50 transition-colors',
                org.is_active && 'bg-primary-50'
              )}
            >
              <div className={cn(
                'h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold',
                org.is_active
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-neutral-100 text-neutral-600'
              )}>
                {orgLabel(org).charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn('font-medium truncate', org.is_active ? 'text-primary-700' : 'text-neutral-800')}>
                  {orgLabel(org)}
                </p>
              </div>
              {org.is_active && <Check className="h-4 w-4 text-primary-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="absolute top-full mt-1 right-0 text-xs text-error-600 bg-white border border-error-200 rounded-lg px-2 py-1 whitespace-nowrap shadow-sm">
          {error}
        </p>
      )}
    </div>
  );
}

export default OrgSwitcher;
